import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFieldArray, useWatch } from 'react-hook-form';
import { PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import { Input } from '#/modules/commonComponents/form/Input';
import { getNestedError } from '#/modules/commonComponents/form/formFieldUtils';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import MtrPickerDialog from '#/modules/imsMgmt/settings/mtr/components/MtrPickerDialog';
import {
  mapMtrPickerRowToPurchaseOrderLine,
  mergePurchaseOrderLinesFromPickerSelection,
} from '#/modules/srmMgmt/purchaseOrder/helper/formSerialize';
import {
  PURCHASE_ORDER_AMENDMENT_LINES_TIP,
} from '#/modules/srmMgmt/purchaseOrder/schemas/purchaseOrderAmendmentSchema';

const LINE_GRID_COLS =
  '@3xl/form-section:grid-cols-[minmax(9rem,1fr)_7rem_7rem_7rem_minmax(7rem,1fr)_2.5rem]';

const LINES_MIN_W = cn(
  'w-full',
  '@3xl/form-section:min-w-[52rem]',
);

const LINE_HEADER_CLASS = cn(
  'sticky top-0 z-10 hidden w-full gap-3 border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  '@3xl/form-section:grid',
  LINE_GRID_COLS,
);

const LINE_ROW_CLASS = cn(
  'grid w-full grid-cols-1 gap-3 px-3 py-3',
  '@3xl/form-section:items-center',
  LINE_GRID_COLS,
);

const PurchaseOrderAmendmentLineRow = ({
  index,
  register,
  errors,
  line,
  onRemove,
}) => {
  const orderQtyId = `lines.${index}.orderQty`;
  const cancelQtyId = `lines.${index}.cancelQty`;
  const unitPriceId = `lines.${index}.unitPrice`;
  const remarkId = `lines.${index}.remark`;
  const orderQtyError = getNestedError(errors, orderQtyId);
  const cancelQtyError = getNestedError(errors, cancelQtyId);
  const unitPriceError = getNestedError(errors, unitPriceId);
  const remarkError = getNestedError(errors, remarkId);

  return (
    <li className={LINE_ROW_CLASS}>
      <div className="min-w-0">
        <p className="@3xl/form-section:hidden text-xs text-gray-500">物料</p>
        <MaterialNameStack name={line.mtrName} code={line.mtrNo} />
      </div>
      <div className="min-w-0">
        <p className="@3xl/form-section:hidden text-xs text-gray-500">訂購數量</p>
        <Input
          id={orderQtyId}
          type="number"
          min={1}
          aria-label={`訂購數量 ${index + 1}`}
          aria-invalid={orderQtyError ? true : undefined}
          title={orderQtyError?.message || undefined}
          className="w-full"
          {...register(orderQtyId)}
        />
      </div>
      <div className="min-w-0">
        <p className="@3xl/form-section:hidden text-xs text-gray-500">取消數量</p>
        <Input
          id={cancelQtyId}
          type="number"
          min={0}
          aria-label={`取消數量 ${index + 1}`}
          aria-invalid={cancelQtyError ? true : undefined}
          title={cancelQtyError?.message || undefined}
          className="w-full"
          {...register(cancelQtyId)}
        />
      </div>
      <div className="min-w-0">
        <p className="@3xl/form-section:hidden text-xs text-gray-500">單價</p>
        <Input
          id={unitPriceId}
          type="number"
          min={0}
          step="any"
          aria-label={`單價 ${index + 1}`}
          aria-invalid={unitPriceError ? true : undefined}
          title={unitPriceError?.message || undefined}
          className="w-full"
          {...register(unitPriceId)}
        />
      </div>
      <div className="min-w-0">
        <p className="@3xl/form-section:hidden text-xs text-gray-500">行備註</p>
        <Input
          id={remarkId}
          type="text"
          aria-label={`行備註 ${index + 1}`}
          aria-invalid={remarkError ? true : undefined}
          title={remarkError?.message || undefined}
          className="w-full"
          {...register(remarkId)}
        />
      </div>
      <div className="flex min-w-0 @3xl/form-section:justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
          aria-label={`移除明細 ${index + 1}`}
          onClick={() => onRemove(index)}
        >
          <PiTrashBold className="size-4" />
        </Button>
      </div>
    </li>
  );
};

PurchaseOrderAmendmentLineRow.propTypes = {
  index: PropTypes.number.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  line: PropTypes.shape({
    mtrNo: PropTypes.string,
    mtrName: PropTypes.string,
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
};

const PurchaseOrderAmendmentLinesEditor = ({
  control,
  register,
  errors,
  getValues,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);

  const { fields, remove, replace } = useFieldArray({
    control,
    name: 'lines',
  });

  const watchedLines = useWatch({ control, name: 'lines', defaultValue: [] });

  const linesForRender = useMemo(
    () => (fields ?? []).map((field, index) => {
      const watched = watchedLines?.[index] ?? {};
      return {
        ...field,
        ...watched,
        id: field.id,
      };
    }),
    [fields, watchedLines],
  );

  const initialSelectedRows = useMemo(
    () => linesForRender
      .filter((line) => line.mtrId)
      .map((line) => ({
        optionMtrId: line.mtrId,
        mtrNo: line.mtrNo,
        name: line.mtrName,
      })),
    [linesForRender],
  );

  function handleOpenPicker() {
    setPickerKey((prev) => prev + 1);
    setPickerOpen(true);
  }

  function handlePickerConfirm(selectedRows) {
    const nextLines = mergePurchaseOrderLinesFromPickerSelection(
      getValues('lines') || [],
      selectedRows,
      (row) => ({
        ...mapMtrPickerRowToPurchaseOrderLine(row),
        cancelQty: 0,
      }),
    );
    replace(nextLines);
  }

  return (
    <section className="@container/form-section pb-6">
      <div className="mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="form-block__title mb-0!">採購明細</h2>
          <Button type="button" size="sm" onClick={handleOpenPicker}>
            選料
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {PURCHASE_ORDER_AMENDMENT_LINES_TIP}
        </p>
      </div>

      {linesForRender.length === 0 ? (
        <p className="text-sm text-muted-foreground">尚無明細；請選料加入。</p>
      ) : (
        <div
          className={cn(
            'create-edit-panel-scroll overflow-x-auto rounded-md border border-gray-300',
            LINES_MIN_W,
          )}
        >
          <div className={LINE_HEADER_CLASS}>
            <span>物料</span>
            <span>訂購數量</span>
            <span>取消數量</span>
            <span>單價</span>
            <span>行備註</span>
            <span className="sr-only">操作</span>
          </div>
          <ul className="m-0 list-none divide-y divide-gray-200 p-0">
            {linesForRender.map((line, index) => (
              <PurchaseOrderAmendmentLineRow
                key={line.id}
                index={index}
                register={register}
                errors={errors}
                line={line}
                onRemove={remove}
              />
            ))}
          </ul>
        </div>
      )}

      <MtrPickerDialog
        key={pickerKey}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        initialSelectedRows={initialSelectedRows}
        onConfirm={handlePickerConfirm}
        title="請選擇採購物料"
        description="多選後確認；已存在於明細者不重複加入。"
      />
    </section>
  );
};

PurchaseOrderAmendmentLinesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  getValues: PropTypes.func.isRequired,
};

export default PurchaseOrderAmendmentLinesEditor;
